import React, { useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
    Divider,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import DateRangePicker from '../../../components/DateRangePicker';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
    fetchFinanceLedger,
    setFinanceLedgerFilters,
} from '../../../store/slices/financeLedgerSlice';

interface LedgerTabProps {
    selectedCustomerId?: number;
    isClient: boolean;
}

const LedgerTab: React.FC<LedgerTabProps> = ({ selectedCustomerId, isClient }) => {
    const dispatch = useAppDispatch();
    const ledgerState = useAppSelector((state) => state.financeLedger);
    const [ledgerRange, setLedgerRange] = useState<{ from?: string; to?: string }>({});
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    const handleLedgerDateChange = (from: string, to: string) => {
        setLedgerRange({ from, to });
        dispatch(setFinanceLedgerFilters({ customerId: selectedCustomerId, fromDate: from, toDate: to }));
    };

    const handleLedgerDateClear = () => {
        setLedgerRange({});
        dispatch(setFinanceLedgerFilters({ customerId: selectedCustomerId, fromDate: undefined, toDate: undefined }));
    };

    React.useEffect(() => {
        if (selectedCustomerId) {
            dispatch(
                fetchFinanceLedger({
                    customerId: selectedCustomerId,
                    fromDate: ledgerRange.from,
                    toDate: ledgerRange.to,
                })
            );
        }
    }, [dispatch, selectedCustomerId, ledgerRange.from, ledgerRange.to]);

    const handleRefresh = () => {
        if (selectedCustomerId) {
            dispatch(
                fetchFinanceLedger({
                    customerId: selectedCustomerId,
                    fromDate: ledgerRange.from,
                    toDate: ledgerRange.to,
                })
            );
        }
    };

    return (
        <Card sx={{ overflow: 'hidden' }}>
            <CardHeader
                title="Financial Ledger"
                subheader={
                    selectedCustomerId
                        ? `Detailed ledger for customer #${selectedCustomerId}`
                        : 'Select a customer to view ledger details.'
                }
                action={
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                    >
                        <DateRangePicker
                            fromDate={ledgerRange.from}
                            toDate={ledgerRange.to}
                            onDateChange={handleLedgerDateChange}
                            onClear={handleLedgerDateClear}
                            variant="inline"
                        />
                        <IconButton onClick={handleRefresh}>
                            <RefreshIcon />
                        </IconButton>
                    </Stack>
                }
            />
            <CardContent>
                {!selectedCustomerId && !isClient && (
                    <Alert severity="info">Choose a customer to load their ledger entries.</Alert>
                )}
                {ledgerState.loading && <CircularProgress />}
                {ledgerState.error && <Alert severity="error">{ledgerState.error}</Alert>}
                {ledgerState.summary && (
                    <Box>
                        <Grid
                            container
                            spacing={2}
                            sx={{ mb: 2 }}
                            alignItems="stretch"
                        >
                            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                                <Card variant="outlined" sx={{ flexGrow: 1 }}>
                                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Customer
                                        </Typography>
                                        <Typography variant="h6">
                                            {ledgerState.summary.customer.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Login ID: {ledgerState.summary.customer.loginId}
                                        </Typography>
                                        {ledgerState.summary.customer.city && (
                                            <Typography variant="body2" color="text.secondary">
                                                City: {ledgerState.summary.customer.city}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                                <Card variant="outlined" sx={{ flexGrow: 1 }}>
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            gap: 1,
                                            height: '100%',
                                        }}
                                    >
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Total Debit
                                        </Typography>
                                        <Typography variant="h5" color="error">
                                            Rs. {ledgerState.summary.totals.debit.toLocaleString()}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                                <Card variant="outlined" sx={{ flexGrow: 1 }}>
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            gap: 1,
                                            height: '100%',
                                        }}
                                    >
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Total Credit
                                        </Typography>
                                        <Typography variant="h5" color="success.main">
                                            Rs. {ledgerState.summary.totals.credit.toLocaleString()}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {isMobile ? (
                            <Stack spacing={2}>
                                {ledgerState.summary.entries.map((entry) => (
                                    <Card key={entry.id} variant="outlined">
                                        <CardContent>
                                            <Stack spacing={1}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        {entry.date}
                                                    </Typography>
                                                    <Typography variant="subtitle2" fontWeight="bold">
                                                        Rs. {entry.balance.toLocaleString()}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body1">
                                                    {entry.description}
                                                </Typography>
                                                <Divider />
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">Debit</Typography>
                                                        <Typography variant="body2" color={entry.debit ? 'error.main' : 'text.primary'}>
                                                            {entry.debit ? `Rs. ${entry.debit.toLocaleString()}` : '-'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ textAlign: 'right' }}>
                                                        <Typography variant="caption" color="text.secondary">Credit</Typography>
                                                        <Typography variant="body2" color={entry.credit ? 'success.main' : 'text.primary'}>
                                                            {entry.credit ? `Rs. ${entry.credit.toLocaleString()}` : '-'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small" sx={{ minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell align="right">Debit</TableCell>
                                            <TableCell align="right">Credit</TableCell>
                                            <TableCell align="right">Balance</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {ledgerState.summary.entries.map((entry) => (
                                            <TableRow key={entry.id}>
                                                <TableCell>{entry.date}</TableCell>
                                                <TableCell>{entry.description}</TableCell>
                                                <TableCell align="right">
                                                    {entry.debit ? `Rs. ${entry.debit.toLocaleString()}` : '-'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {entry.credit ? `Rs. ${entry.credit.toLocaleString()}` : '-'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    Rs. {entry.balance.toLocaleString()}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table >
                            </Box >
                        )}
                    </Box >
                )}
            </CardContent >
        </Card >
    );
};

export default LedgerTab;
